import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui";
import { Book, Star, Calendar, Heart } from "lucide-react";
import { useUnsplashImages } from "@/hooks/useUnsplashImages";
import { Link } from 'react-router-dom';

const AfterSchool = () => {
  const [heroImage, setHeroImage] = useState("");
  const { data: afterSchoolImages } = useUnsplashImages("after school activities children", 1);
  
  useEffect(() => {
    if (afterSchoolImages && afterSchoolImages.length > 0) {
      setHeroImage(afterSchoolImages[0].urls.regular);
    }
  }, [afterSchoolImages]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>After-School Program Management - Hivetivity</title>
        <meta name="description" content="Streamline your after-school program management with Hivetivity. Coordinate activities, track attendance, and communicate with parents easily." />
      </Helmet>

      <TopNavbar />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-yellow-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#5a5a59] mb-6">
                  Empower Your After-School Program
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Create enriching after-school experiences while we handle the administrative tasks with our comprehensive management platform.
                </p>
                <Link to="/business-trial">
                  <Button className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-6 text-lg">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
              <div className="relative">
                {heroImage ? (
                  <OptimizedImage 
                    src={heroImage}
                    alt="Children in after-school activities"
                    width={600}
                    height={400}
                    className="rounded-2xl shadow-xl"
                    objectFit="cover"
                  />
                ) : (
                  <div className="rounded-2xl bg-gray-200 w-full h-[400px] animate-pulse flex items-center justify-center">
                    <p className="text-gray-500">Loading image...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-[#5a5a59] mb-12">
              Everything Your After-School Program Needs
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="p-6 rounded-xl bg-yellow-50 hover:bg-yellow-100 transition-colors">
                <Book className="w-12 h-12 text-yellow-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Program Planning</h3>
                <p className="text-gray-600">Design and manage diverse activities and learning opportunities.</p>
              </div>
              <div className="p-6 rounded-xl bg-yellow-50 hover:bg-yellow-100 transition-colors">
                <Star className="w-12 h-12 text-yellow-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Student Progress</h3>
                <p className="text-gray-600">Track achievements and development milestones.</p>
              </div>
              <div className="p-6 rounded-xl bg-yellow-50 hover:bg-yellow-100 transition-colors">
                <Calendar className="w-12 h-12 text-yellow-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Smart Scheduling</h3>
                <p className="text-gray-600">Coordinate activities, staff, and facility usage.</p>
              </div>
              <div className="p-6 rounded-xl bg-yellow-50 hover:bg-yellow-100 transition-colors">
                <Heart className="w-12 h-12 text-yellow-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Parent Communication</h3>
                <p className="text-gray-600">Keep families informed and engaged with regular updates.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-20 bg-yellow-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-[#5a5a59] mb-8">
                Trusted by After-School Programs
              </h2>
              <blockquote className="text-xl text-gray-600 italic mb-6">
                "Hivetivity has transformed how we manage our after-school program. The scheduling and communication features are invaluable."
              </blockquote>
              <p className="font-semibold text-[#5a5a59]">
                - Robert Martinez, Program Director
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-[#5a5a59] mb-6">
              Ready to Transform Your After-School Program?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join hundreds of after-school programs using Hivetivity to create engaging and enriching experiences.
            </p>
            <Link to="/business-trial">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-6 text-lg">
                Start Your Free Trial Today
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AfterSchool;
